import { BEIGE_300, BEIGE_600, GRAY_700 } from 'constants/styling/theme'
import { PurchaseFlowProductOption, usePurchaseFlowProducts } from 'components/pages/PurchaseFlow/_main/contexts/PurchaseFlowProducts.context'
import { bigFromFee, formatPrice, valueAfterDiscount, valueMultiplied } from 'utils/price'
import { useCart, usePurchaseFlowConfig } from 'components/pages/PurchaseFlow/_main/contexts'

import { bigFromDecimal } from 'utils/price/decimals'
import { useEmbedConfig } from 'components/pages/PurchaseFlow/_main/contexts/EmbedConfig.context'

import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import Collapse from '@mui/material/Collapse'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import { ProductItemSummary } from './ProductItemSummary'
import React from 'react'
import { TransitionGroup } from 'react-transition-group'
import Typography from '@mui/material/Typography'
import { standardTimeoutConfig } from 'utils/animations'
import { DocumentSelectionGroup } from 'constants/product'
import { useTranslation } from 'react-i18next'

/**
 * @interface Props ProductList properties
 */
interface Props {
  /** Product ids whose lines (incl. their options) are not listed — e.g. the measurement product replaced by the cost-assumption panel (ISSUE-2188) */
  hiddenProductIds?: number[]
}

/**
 * @component List of selected products with prices
 * @example <ProductList />
 */
export const ProductList: React.FC<Props> = ({ hiddenProductIds = [] }) => {
  const { t } = useTranslation(['order'])

  const { catalogueCurrency, catalogueDiscount } = usePurchaseFlowConfig()
  const { selectedProductOptions } = usePurchaseFlowProducts()
  const { cartProductList } = useCart()
  const { isEmbed } = useEmbedConfig()

  const getOptionPriceAfterDiscount = (option: PurchaseFlowProductOption) => {
    return valueAfterDiscount(option.feePrice, catalogueDiscount)
  }

  return (
    <TransitionGroup component={null}>
      {cartProductList.length > 0
        ? (
          <Collapse in={cartProductList.length > 0} timeout={standardTimeoutConfig}>
            <List disablePadding>
              <TransitionGroup component={null}>
                {cartProductList.filter(({ id }) => !hiddenProductIds.includes(id)).map(({ id, quantity, feePrice, originalPrice, documentSelectionGroup }) => {
                  // Embed OneSystem/channel discount: strike the base (public) price when it exceeds
                  // the effective price. Takes precedence over the org-discount branch for these lines.
                  // Comparison is on Big values, never on formatted-price strings.
                  const embedStruck = isEmbed && !!originalPrice &&
                    bigFromDecimal(originalPrice.value).gt(bigFromDecimal(feePrice.value))

                  return (
                  <Collapse key={id} in={!!quantity && quantity > 0} timeout={standardTimeoutConfig}>
                    <ListItem disablePadding>
                      <ListItemText
                        disableTypography
                        primary={
                          <>
                            <ProductItemSummary
                              name={t(`product:p_${id}`)}
                              price={
                                embedStruck && originalPrice
                                  ? formatPrice(valueMultiplied(originalPrice, quantity ?? 1), catalogueCurrency)
                                  : formatPrice(valueMultiplied(feePrice, quantity ?? 1), catalogueCurrency)
                              }
                              quantity={quantity}
                              priceAfterDiscount={
                                embedStruck
                                  ? formatPrice(valueMultiplied(feePrice, quantity ?? 1), catalogueCurrency)
                                  : catalogueDiscount.gt(0)
                                    ? formatPrice(
                                      valueMultiplied(valueAfterDiscount(feePrice, catalogueDiscount), quantity ?? 1),
                                      catalogueCurrency
                                    )
                                    : undefined
                              }
                              emphasizeAfterDiscount={
                                embedStruck && bigFromDecimal(feePrice.value).eq(0) ? 'covered' : undefined
                              }
                            />
                            {documentSelectionGroup === DocumentSelectionGroup.BEWILLIGUNGSURKUNDE &&
                              <Typography variant="text-sm" color={GRAY_700} component="div">
                                {t('step_product.bewilligungsurkunde_included_extract')}
                              </Typography>
                            }
                          </>
                        }
                        secondary={
                          <TransitionGroup component={null}>
                            {!!selectedProductOptions[id] && Object.values(selectedProductOptions[id]).length > 0 &&
                              Object.values(selectedProductOptions[id]).map(option =>
                                option.quantity > 0 && (
                                  <Collapse key={option.id} in={option.quantity > 0} timeout={standardTimeoutConfig}>
                                    <ProductItemSummary
                                      key={option.id}
                                      type='secondary'
                                      quantity={option.quantity}
                                      name={t(`product:p_${option.id}`)}
                                      price={formatPrice(
                                        valueMultiplied(bigFromFee(option.feePrice), option.quantity),
                                        catalogueCurrency
                                      )}
                                      priceAfterDiscount={
                                        catalogueDiscount.gt(0)
                                          ? formatPrice(
                                            valueMultiplied(getOptionPriceAfterDiscount(option), option.quantity),
                                            catalogueCurrency
                                          )
                                          : undefined
                                      }
                                    />
                                  </Collapse>
                                )
                              )}
                          </TransitionGroup>
                        }
                      />
                    </ListItem>
                  </Collapse>
                  )
                })}
              </TransitionGroup>
            </List>

          </Collapse>
        )
        : (
          <Collapse in={cartProductList.length === 0} timeout={standardTimeoutConfig}>

            {/** NO PRODUCTS SELECTED */}
            <BorderBoxWrapper
              padding={1}
              elevation="none"
              borderColor={BEIGE_600}
              backgroundColor={BEIGE_300}
            >

              <Typography variant='text-sm'>
                {t('cart.no_product_selected')}
              </Typography>

            </BorderBoxWrapper>

          </Collapse>
        )
      }
    </TransitionGroup>
  )
}
